import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api/client";

export type TemplateSource = "Xamun Global" | "This workspace" | "Pax8 partner";

export interface TemplateMeta {
  id?: string;
  industry: string;
  trade: string;
  archetype: string;
  version: string;
  name: string;
  description: string;
  // Provenance — drives the SOURCE filter row + per-card source pill.
  // The API will surface this once template_catalog has a source column;
  // for V1 we synthesize at read time so the UI matches the design.
  source?: TemplateSource;
  // Pattern-library calibration metrics (synthesized when omitted).
  priors?: number;
  hitRatePct?: number;
  avgLiftPct?: number;
  stepsCount?: number;
  gatesCount?: number;
  durationWeeks?: number;
  requiredAgents?: string[];
  usedInWorkspace?: { playId: string; playName: string; status: string } | null;
}

export interface TemplatesResponse {
  templates: TemplateMeta[];
}

export interface TemplateFilter {
  industry?: string;
  trade?: string;
  archetype?: string;
}

function toQuery(filter: TemplateFilter): string {
  const sp = new URLSearchParams();
  if (filter.industry) sp.set("industry", filter.industry);
  if (filter.trade) sp.set("trade", filter.trade);
  if (filter.archetype) sp.set("archetype", filter.archetype);
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useTemplates(filter: TemplateFilter = {}) {
  const q = toQuery(filter);
  return useQuery<TemplatesResponse, Error>({
    queryKey: ["templates", filter.industry ?? null, filter.trade ?? null, filter.archetype ?? null],
    queryFn: () => apiGet<TemplatesResponse>(`/api/templates${q}`),
    staleTime: 60_000,
  });
}

export interface TemplatePreviewResponse {
  meta: TemplateMeta;
  graph: {
    nodes: Array<{
      id: string;
      title: string;
      agentCapability?: string;
      requiresApproval?: boolean;
      description?: string;
      inputs?: Record<string, unknown>;
    }>;
    edges: Array<{ from: string; to: string }>;
  };
  outcome: {
    statement: string;
    metrics: Array<{ label: string; target: string; baseline?: string; measurement_window?: string }>;
  } | null;
}

/**
 * Read-only template preview for the planner's Editor stage. Templates
 * are immutable artifacts so the response is heavily cacheable — we
 * use Infinity stale time and the registry-derived templateId as the
 * key.
 */
export function useTemplatePreview(
  industry: string | undefined,
  trade: string | undefined,
  archetype: string | undefined,
) {
  return useQuery<TemplatePreviewResponse, Error>({
    queryKey: ["template-preview", industry, trade, archetype],
    queryFn: () =>
      apiGet<TemplatePreviewResponse>(
        `/api/templates/${encodeURIComponent(industry!)}/${encodeURIComponent(trade!)}/${encodeURIComponent(archetype!)}/preview`,
      ),
    enabled: !!(industry && trade && archetype),
    staleTime: Infinity,
  });
}
